import { Box, Button, Typography } from '@mui/material'
import colors from '../../../constants/colors'
import FormLayout from '../FormLayout'
import AddIcon from '@mui/icons-material/Add'
import { useForm, get } from 'react-hook-form'
import TextareaInput from '../../TextareaInput'
import { PhotoCamera } from '@mui/icons-material'
import { ChangeEvent, FC, Fragment, useCallback, useEffect, useState } from 'react'
import TextInput from '../../TextInput'
import Dropdown from '../../Dropdown'
import selectOptions from '../../../constants/selectOptions'
import { formTypes, IReportingPartyForm } from './types'
import constants from './constants'
import { vestResolver } from '@hookform/resolvers/vest'
import utils from './utils'
import ModalTAG from '../../ModalTAG'
import CardTAG from '../../CardTAG'
import DateRangePicker from '../../DateRangePicker'

const ReportingPartyForm: FC<IReportingPartyForm> = ({
  updateNumberOfChanges,
  submitFunctions,
  submitFunctionName = '',
  defaultValue,
  previewOnly = false,
}) => {
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [openModal, setOpenModal] = useState(false)
  const [customFields, setCustomFields] = useState<string[]>()
  const [changes, setChanges] = useState(0)
  const [originalHireDate, setOriginalHireDate] = useState(null)
  const [terminationDate, setTerminationDate] = useState(null)

  const formMethods = useForm<any>({
    defaultValues: {},
    mode: 'onChange',
    shouldFocusError: false,
  })

  const { register, formState, control, watch, getValues, reset, setValue } = formMethods
  const { dirtyFields } = formState
  const changesNumber = Object.keys(dirtyFields).length

  // const submitHandler = () => {
  //   const { image, ...restOfValues } = getValues()
  //   if (Object.keys(dirtyFields).length > 0) {
  //     return {
  //       reporting_party: {
  //         ...restOfValues,
  //         ...(image instanceof File ? image : {}),
  //         original_hire_date: originalHireDate,
  //         termination_date: terminationDate,
  //       },
  //       id: defaultValue?.id,
  //       custom_fields: Object.keys(customFieldsGetValue()).map(key => {
  //         return {
  //           title: key,
  //           description: customFieldsGetValue()[key],
  //         }
  //       }),
  //     }
  //   }
  // }
  const submitHandler = () => {
    const { image, ...restOfValues } = getValues();

    if (Object.keys(dirtyFields).length > 0) {
      const newObject = {
        reporting_party: {
          ...restOfValues,
          original_hire_date: originalHireDate,
          termination_date: terminationDate,
        },
        id: defaultValue?.id,
        custom_fields: Object.keys(customFieldsGetValue()).map(key => ({
          title: key,
          description: customFieldsGetValue()[key],
        })),
      };

      // ✅ Attach image if it’s a File
      if (image instanceof File) {
        return {
          ...newObject,
          reporting_party: {
            ...newObject.reporting_party,
            image: image,
          },
        };
      }

      return newObject;
    }
  };

  submitFunctions[submitFunctionName].submitFunction = submitHandler

  const imageWatchValue = watch('image')
  const subjectTypeWatchValue = watch('person_type') as string

  const addNewTagFormMethods = useForm<any>({
    defaultValues: { newTag: '' },
    mode: 'onChange',
    shouldFocusError: false,
    resolver: vestResolver(utils.newTagFormValidation),
  })
  const {
    register: newTagRegister,
    handleSubmit: newTagHandleSubmit,
    formState: formStateNewTag,
    reset: resetNewTag,
  } = addNewTagFormMethods

  const customFieldsFormMethods = useForm<any>({
    defaultValues: {},
    mode: 'onChange',
    shouldFocusError: false,
  })

  const {
    register: customFieldsRegister,
    formState: formStateCustomFields,
    reset: resetCustomFields,
    getValues: customFieldsGetValue,
  } = customFieldsFormMethods

  const { dirtyFields: dirtyFieldsCustomFields } = formStateCustomFields
  const changesNumberCustomFields = Object.keys(dirtyFieldsCustomFields).length

  useEffect(() => {
    const counter = changesNumber + changesNumberCustomFields
    if (counter == changes) return
    if (counter !== changes) {
      updateNumberOfChanges(counter - changes, submitFunctionName)
      setChanges(counter)
    }
  }, [formState, formStateCustomFields])

  useEffect(() => {
    if (
      subjectTypeWatchValue &&
      subjectTypeWatchValue !== defaultValue?.reporting_party?.person_type
    ) {
      const newReportingPartyValue = {
        ...constants.defaulfFormValues[subjectTypeWatchValue],
        person_type: subjectTypeWatchValue,
      }
      reset(newReportingPartyValue)
    }
  }, [subjectTypeWatchValue, defaultValue])

  useEffect(() => {
    if (defaultValue) {
      const defaultValues = defaultValue ?? {
        person_type: '',
      }

      reset(defaultValues?.reporting_party, {
        keepDirty: true,
        keepDirtyValues: true,
      })
      setValue('image', defaultValue?.reporting_party?.image as string)
      const fields = defaultValue?.custom_fields?.map((field: { title: any }) => field?.title)
      const transformedObject = defaultValue?.custom_fields?.reduce(
        (result: { [x: string]: any }, item: { title: any; description: any }) => {
          const key = item?.title
          result[key] = item?.description
          return result
        },
        {},
      )
      resetCustomFields(transformedObject, {
        keepDirty: true,
        keepDirtyValues: true,
      })
      setCustomFields(fields)
    }
  }, [defaultValue])

  useEffect(() => {
    if (typeof imageWatchValue === 'string') {
      setImagePreview(imageWatchValue === '' ? '' : imageWatchValue)
      return
    }
    if (imageWatchValue instanceof File) {
      setImagePreview(URL.createObjectURL(imageWatchValue))
    }
  }, [imageWatchValue])

  const getMessage = useCallback(
    (field: formTypes) => {
      const error = get(formState.errors, field)

      if (!error) {
        return ''
      }

      const { message, ...nestedError } = error
      const errorType = typeof error

      if (errorType === 'string') {
        return error
      }

      if (errorType === 'object' && message) {
        return message
      }

      const nestedField = Object.keys(nestedError)[0]
      const nestedErrorMessage = nestedError[nestedField]?.message || ''
      return nestedErrorMessage
    },
    [formState],
  )
  const getMessageNewTag = useCallback(
    (field: 'newTag') => {
      if (!formStateNewTag.errors[field]) return ''

      return formStateNewTag.errors[field]!.message
    },
    [formState],
  )

  const addNewTagSubmitHandler = (values: { newTag: string }) => {
    const oldTags = customFieldsGetValue()
    const { newTag } = values
    const propertyName = utils.camalize(newTag)
    const newState = {
      ...oldTags,
      [propertyName]: '',
    }
    setOpenModal(false)
    const updatedCustomFields = customFields ? [...customFields, newTag] : [newTag]
    setCustomFields(updatedCustomFields)
    resetCustomFields(newState, {
      keepDirty: true,
      keepDirtyValues: true,
    })
    resetNewTag({ newTag: '' })
  }

  const openModalHandler = () => {
    setOpenModal(true)
  }

  const renderFields = () => {
    return constants.textFieldBasedOnSubjectType[subjectTypeWatchValue].map(field => {
      if (!['original_hire_date', 'termination_date'].includes(field.fieldName)) {
        return (
          <TextInput
            register={register}
            {...field}
            error={getMessage(field.fieldName) as string}
            key={field.fieldName}
            disabled={previewOnly}
          />
        )
      }
      if (field.fieldName === 'original_hire_date') {
        return (
          <Fragment key={field.fieldName}>
            <DateRangePicker
              label={field.label}
              disablePresets={true}
              setDate={setOriginalHireDate}
              date={originalHireDate}
              name={field.fieldName}
            />
          </Fragment>
        )
      }

      if (field.fieldName === 'termination_date') {
        return (
          <Fragment key={field.fieldName}>
            <DateRangePicker
              label={field.label}
              disablePresets={true}
              setDate={setTerminationDate}
              date={terminationDate}
              name={field.fieldName}
            />
          </Fragment>
        )
      }
    })
  }

  const renderCustomFields = () => {
    const customFieldsValues = customFieldsGetValue()
    return (
      customFields &&
      Object.keys(customFieldsValues).map(key => {
        return (
          <TextareaInput
            label={key}
            key={key}
            register={customFieldsRegister}
            fieldName={key}
            minRows={5}
            placeholder={'Write a brief message'}
            previewOnly={previewOnly}
          />
        )
      })
    )
  }

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e?.target?.files) {
      const file = e?.target?.files[0]
      if (file) {
        setValue('image', file, { shouldDirty: true });
        updateNumberOfChanges(1, 'reportingParty:');
      }
      // setValue('image', file) // Set the value of the "file" field to the selected File object
    }
  }

  const resolveForm = () => {
    if (!subjectTypeWatchValue) {
      return (
        <Dropdown
          control={control}
          label='Subject Type'
          fieldName={'person_type'}
          options={selectOptions.subjectTypes}
          selectedValue={subjectTypeWatchValue}
          placeholder='Select a Option'
          previewOnly={previewOnly}
        />
      )
    }
    return (
      <>
        <Box className='t-hbox t-gap-18'>
          <Box sx={constants.imageUploadRowStyle}>
            {!imagePreview && (
              <Box sx={constants.imageMessageStyle}>
                <Typography sx={{ textAlign: 'center' }}>Upload Profile Image</Typography>
              </Box>
            )}
            {imagePreview && (
              <Box sx={{ width: { xs: '100%' }, height: { xs: '100%' } }}>
                <img
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    borderRadius: '5px',
                  }}
                  src={imagePreview}
                />
              </Box>
            )}
            <Button
              variant='submitFormButton'
              component='label'
              sx={{ mt: '18px' }}
              endIcon={<PhotoCamera />}
              disabled={previewOnly}
            >
              <Typography variant='bodyBold'>
                {imagePreview ? 'Change Image' : 'Upload Image'}
              </Typography>
              <input onChange={handleFileChange} hidden multiple accept='image/*' type='file' />
            </Button>
          </Box>
          <Box className={'t-vbox t-gap-18 t-flex-1'}>
            <Dropdown
              control={control}
              label='Subject Type'
              fieldName={'person_type'}
              options={selectOptions.subjectTypes}
              selectedValue={subjectTypeWatchValue}
              placeholder='Select a Option'
              previewOnly={previewOnly}
            />
            {renderFields()}
            {subjectTypeWatchValue !== 'CURRENT_EMPLOYEE' &&
              subjectTypeWatchValue !== 'CURRENT_CONTRACTOR' &&
              subjectTypeWatchValue !== 'FORMER_EMPLOYEE' &&
              subjectTypeWatchValue !== 'FORMER_CONTRACTOR' && (
                <TextareaInput
                  label={'Description'}
                  register={register}
                  fieldName={'description'}
                  minRows={5}
                  placeholder={'Write a brief message'}
                  previewOnly={previewOnly}
                />
              )}
            {renderCustomFields()}
            <Button
              variant='outlined'
              startIcon={<AddIcon />}
              sx={{ color: colors.BLACK }}
              onClick={openModalHandler}
              disabled={previewOnly}
            >
              <Typography variant='bodyRegular' color='inherit'>
                Add Custom Text Field
              </Typography>
            </Button>
          </Box>
        </Box>
        <ModalTAG open={openModal}>
          <form onSubmit={newTagHandleSubmit(addNewTagSubmitHandler)}>
            <CardTAG
              title={'Add Custom Field Title'}
              closable={true}
              closeCard={() => {
                setOpenModal(false)
              }}
            >
              <FormLayout>
                <TextInput
                  register={newTagRegister}
                  fieldName={'newTag'}
                  label={'Title'}
                  error={getMessageNewTag('newTag') as string}
                />
                <Button variant='submitFormButton' type='submit'>
                  <Typography variant='bodyBold'>Add</Typography>
                </Button>
              </FormLayout>
            </CardTAG>
          </form>
        </ModalTAG>
      </>
    )
  }

  return <>{resolveForm()}</>
}

export default ReportingPartyForm
